import React from "react";
import { call, put, takeLatest, fork, all } from "redux-saga/effects";
import * as Types from "../constants/Types";
import * as Func from "../helpers/functions";
import * as AutoTranslateService from "../services/autoTranslateService";
import * as AutoTranslateActions from "../actions/autoTranslateAction";
import * as TourService from "../services/tourService";
import { message } from "antd";
import { PATHS } from "../constants/Define";
import i18n from "../constants/initLng";
import history from "../helpers/history";

function successHandleAutoTranslate(action, entityType) {
  return Func.sagaSuccessMessage({
    action,
    message: `${i18n.t("notification.autoTranslateSuccess")}`,
  });
}

function errorHandleAutoTranslate(errorActionType) {
  return function* (error) {
    const errorMessage =
      error?.errors?.[0]?.message ||
      error?.message ||
      i18n.t(`validation.statusCode.code.UNDEFINED_ERROR`);

    Func.handleErrorMessage(
      error,
      errorActionType,
      i18n.t("notification.autoTranslateFailed")
    );

    yield put({
      type: errorActionType,
      error: errorMessage,
    });
  };
}

function* campaignAutoTranslateSaga() {
  yield takeLatest(Types.CAMPAIGN_AUTO_TRANSLATE_REQUEST, function* (action) {
    try {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.campaignAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      if (result && result.status && result.status !== 200) {
        throw result;
      }

      successHandleAutoTranslate(action, "campaign");
      yield put({ type: Types.CAMPAIGN_AUTO_TRANSLATE_SUCCESS, result });

      history.push(PATHS.CAMPAIGN.LIST.path);
      history.go();
    } catch (error) {
      yield* errorHandleAutoTranslate(Types.CAMPAIGN_AUTO_TRANSLATE_FAILURE)(
        error
      );
    }
  });
}

function* hotelAutoTranslateSaga() {
  yield takeLatest(
    Types.HOTEL_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.hotelAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "hotel");
      yield put({ type: Types.HOTEL_AUTO_TRANSLATE_SUCCESS, result });
      window.location.reload();
    }, errorHandleAutoTranslate(Types.HOTEL_AUTO_TRANSLATE_FAILURE))
  );
}

function* roomTypeAutoTranslateSaga() {
  yield takeLatest(
    Types.ROOM_TYPE_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.roomTypeAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "room type");
      yield put({ type: Types.ROOM_TYPE_AUTO_TRANSLATE_SUCCESS, result });
      window.location.reload();
    }, errorHandleAutoTranslate(Types.HOTEL_AUTO_TRANSLATE_FAILURE))
  );
}

function* tourAutoTranslateSaga() {
  yield takeLatest(
    Types.TOUR_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.tourAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "tour");
      yield put({ type: Types.TOUR_AUTO_TRANSLATE_SUCCESS, result });
      yield put({ type: Types.TOUR_DETAIL_ACTION, id: sourceId });
    }, errorHandleAutoTranslate(Types.TOUR_AUTO_TRANSLATE_FAILURE))
  );
}

function* tourTicketAutoTranslateSaga() {
  yield takeLatest(
    Types.TOUR_TICKET_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.tourTicketAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "tour ticket");
      yield put({ type: Types.TOUR_TICKET_AUTO_TRANSLATE_SUCCESS, result });

      const resultTourTicket = yield call(
        TourService.getDetailTourTicketDetail,
        sourceId
      );
      yield put({
        type: Types.DETAIL_TOUR_TICKET_DETAIL_SUCCESS,
        tourTicket: resultTourTicket,
      });
    }, errorHandleAutoTranslate(Types.TOUR_TICKET_AUTO_TRANSLATE_FAILURE))
  );
}

function* supplierAutoTranslateSaga() {
  yield takeLatest(
    Types.SUPPLIER_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages, callbacks } = action;

      const result = yield call(AutoTranslateService.supplierAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "supplier");
      yield put({ type: Types.SUPPLIER_AUTO_TRANSLATE_SUCCESS, result });

      if (callbacks?.onSuccess) {
        yield callbacks.onSuccess();
      }
    }, errorHandleAutoTranslate(Types.SUPPLIER_AUTO_TRANSLATE_FAILURE))
  );
}

function* menuAutoTranslateSaga() {
  yield takeLatest(
    Types.MENU_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages, callbacks } = action;

      const result = yield call(AutoTranslateService.menuAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "menu");
      yield put({ type: Types.MENU_AUTO_TRANSLATE_SUCCESS, result });

      if (callbacks?.onSuccess) {
        yield callbacks.onSuccess();
      }
    }, errorHandleAutoTranslate(Types.MENU_AUTO_TRANSLATE_FAILURE))
  );
}

function* tagGroupDetailAutoTranslateSaga() {
  yield takeLatest(
    Types.TAG_GROUP_DETAIL_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages, callbacks } = action;

      const result = yield call(
        AutoTranslateService.tagGroupDetailAutoTranslate,
        { sourceId, sourceLanguage, targetLanguages }
      );

      successHandleAutoTranslate(action, "tag group detail");
      yield put({
        type: Types.TAG_GROUP_DETAIL_AUTO_TRANSLATE_SUCCESS,
        result,
      });
      if (callbacks?.onSuccess) {
        yield callbacks.onSuccess();
      }
    }, errorHandleAutoTranslate(Types.TAG_GROUP_DETAIL_AUTO_TRANSLATE_FAILURE))
  );
}

function* vinWonderTagGroupAutoTranslateSaga() {
  yield takeLatest(
    Types.VINWONDER_TAG_GROUP_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages, callbacks } = action;

      const result = yield call(
        AutoTranslateService.vinWonderTagGroupAutoTranslate,
        { sourceId, sourceLanguage, targetLanguages }
      );

      successHandleAutoTranslate(action, "vinwonder tag group");
      yield put({
        type: Types.VINWONDER_TAG_GROUP_AUTO_TRANSLATE_SUCCESS,
        result,
      });
      if (callbacks?.onSuccess) {
        yield callbacks.onSuccess();
      }
    }, errorHandleAutoTranslate(
      Types.VINWONDER_TAG_GROUP_AUTO_TRANSLATE_FAILURE
    ))
  );
}

function* ratePlanAutoTranslateSaga() {
  yield takeLatest(
    Types.RATE_PLAN_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(AutoTranslateService.ratePlanAutoTranslate, {
        sourceId,
        sourceLanguage,
        targetLanguages,
      });

      successHandleAutoTranslate(action, "rate plan");
      yield put({ type: Types.RATE_PLAN_AUTO_TRANSLATE_SUCCESS, result });
    }, errorHandleAutoTranslate(Types.RATE_PLAN_AUTO_TRANSLATE_FAILURE))
  );
}

function* bookablePackageRateAutoTranslateSaga() {
  yield takeLatest(
    Types.BOOKABLE_PACKAGE_RATE_AUTO_TRANSLATE_REQUEST,
    Func.sagaWrapper(function* (action) {
      const { sourceId, sourceLanguage, targetLanguages } = action;

      const result = yield call(
        AutoTranslateService.bookablePackageRateAutoTranslate,
        { sourceId, sourceLanguage, targetLanguages }
      );

      successHandleAutoTranslate(action, "bookable package rate");
      yield put({
        type: Types.BOOKABLE_PACKAGE_RATE_AUTO_TRANSLATE_SUCCESS,
        result,
      });
      yield put({ type: Types.BOOKABLE_PACKAGE_RATE_DETAIL_ACTION, id: sourceId });
    }, errorHandleAutoTranslate(
      Types.BOOKABLE_PACKAGE_RATE_AUTO_TRANSLATE_FAILURE
    ))
  );
}

export function* autoTranslateSaga() {
  yield all([
    fork(campaignAutoTranslateSaga),
    fork(hotelAutoTranslateSaga),
    fork(tourAutoTranslateSaga),
    fork(tourTicketAutoTranslateSaga),
    fork(supplierAutoTranslateSaga),
    fork(menuAutoTranslateSaga),
    fork(tagGroupDetailAutoTranslateSaga),
    fork(vinWonderTagGroupAutoTranslateSaga),
    fork(ratePlanAutoTranslateSaga),
    fork(bookablePackageRateAutoTranslateSaga),
    fork(roomTypeAutoTranslateSaga),
  ]);
}

export default autoTranslateSaga;
